import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { problemDetailCode, toFormError } from '@rms/core/error';
import { AppError } from '@rms/sdk/errors';
import { PageableParam, Paginated } from '@rms/sdk/models';
import { Filter } from '@rms/ui/filters';
import { catchError, map, Observable, throwError } from 'rxjs';

import { PartnerEmailInUseError, PartnerSlugInUseError } from '../errors';
import {
  Partner,
  PARTNER_ERROR_CODE,
  PARTNER_USER_ROLE,
  PartnerCreateRequest,
  PartnerPatchRequest,
  PartnerRole,
  PartnerUser,
  PartnerUserCreateRequest,
  PartnerUserResponse,
  PartnerUsersScope,
  PartnerUserUpdateRequest,
} from '../models';
import { PartnerApiService } from './partner-api.service';

@Injectable({
  providedIn: 'root',
})
export class PartnerService {
  private readonly api = inject(PartnerApiService);

  public getPartners(pageable: PageableParam, filters: Filter[] = []): Observable<Paginated<Partner>> {
    return this.api
      .getPartners(pageable, filters)
      .pipe(
        catchError((error: HttpErrorResponse) => throwError(() => AppError.from(error, 'partners.errors.load-list')))
      );
  }

  public getPartner(partnerId: string): Observable<Partner> {
    return this.api
      .getPartner(partnerId)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError(() =>
            AppError.from(
              error,
              error.status === HttpStatusCode.NotFound ? 'partners.errors.not-found' : 'partners.errors.load'
            )
          )
        )
      );
  }

  public getPartnerUsers(
    scope: PartnerUsersScope,
    pageable: PageableParam,
    filters: Filter[] = []
  ): Observable<Paginated<PartnerUser>> {
    return this.api.getPartnerUsers(scope, pageable, filters).pipe(
      map((page) => ({ ...page, content: page.content.map(toPartnerUser) })),
      catchError((error: HttpErrorResponse) => throwError(() => AppError.from(error, 'partners.errors.load-users')))
    );
  }

  public createPartnerUser(scope: PartnerUsersScope, request: PartnerUserCreateRequest): Observable<PartnerUser> {
    return this.api.createPartnerUser(scope, request).pipe(
      map(toPartnerUser),
      catchError((error: HttpErrorResponse) => {
        switch (problemDetailCode(error)) {
          case PARTNER_ERROR_CODE.EMAIL_ALREADY_IN_USE:
            return throwError(() => new PartnerEmailInUseError(error));
          case PARTNER_ERROR_CODE.PARTNER_DISABLED:
            return throwError(() => AppError.from(error, 'partners.errors.user-create-disabled'));
        }

        return throwError(() => toFormError(error, 'partners.errors.user-create'));
      })
    );
  }

  public updatePartnerUser(
    scope: PartnerUsersScope,
    userId: string,
    request: PartnerUserUpdateRequest
  ): Observable<PartnerUser> {
    return this.api.updatePartnerUser(scope, userId, request).pipe(
      map(toPartnerUser),
      catchError((error: HttpErrorResponse) => {
        if (problemDetailCode(error) === PARTNER_ERROR_CODE.EMAIL_ALREADY_IN_USE) {
          return throwError(() => new PartnerEmailInUseError(error));
        }

        return throwError(() =>
          error.status === HttpStatusCode.NotFound
            ? AppError.from(error, 'partners.errors.user-not-found')
            : toFormError(error, 'partners.errors.user-update')
        );
      })
    );
  }

  public getPartnerRoles(scope: PartnerUsersScope): Observable<PartnerRole[]> {
    return this.api
      .getPartnerRoles(scope)
      .pipe(
        catchError((error: HttpErrorResponse) => throwError(() => AppError.from(error, 'partners.errors.load-roles')))
      );
  }

  public updatePartnerUserRole(scope: PartnerUsersScope, userId: string, roleId: string): Observable<PartnerUser> {
    return this.api.updatePartnerUserRole(scope, userId, [roleId]).pipe(
      map(toPartnerUser),
      catchError((error: HttpErrorResponse) => throwError(() => toUserError(error, 'partners.errors.user-role')))
    );
  }

  public setPartnerUserPassword(partnerId: string, userId: string, password: string): Observable<void> {
    return this.api
      .setPartnerUserPassword(partnerId, userId, password)
      .pipe(
        catchError((error: HttpErrorResponse) => throwError(() => toUserError(error, 'partners.errors.user-password')))
      );
  }

  public deletePartnerUser(scope: PartnerUsersScope, userId: string): Observable<void> {
    return this.api
      .deletePartnerUser(scope, userId)
      .pipe(
        catchError((error: HttpErrorResponse) => throwError(() => AppError.from(error, 'partners.errors.user-delete')))
      );
  }

  public createPartner(request: PartnerCreateRequest): Observable<Partner> {
    return this.api.createPartner(request).pipe(
      catchError((error: HttpErrorResponse) => {
        switch (problemDetailCode(error)) {
          case PARTNER_ERROR_CODE.SLUG_ALREADY_IN_USE:
            return throwError(() => new PartnerSlugInUseError(error));
          case PARTNER_ERROR_CODE.EMAIL_ALREADY_IN_USE:
            return throwError(() => new PartnerEmailInUseError(error));
        }

        return throwError(() => toFormError(error, 'partners.errors.create'));
      })
    );
  }

  public updatePartner(partnerId: string, request: PartnerPatchRequest): Observable<Partner> {
    return this.api
      .updatePartner(partnerId, request)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError(() =>
            error.status === HttpStatusCode.NotFound
              ? AppError.from(error, 'partners.errors.not-found')
              : toFormError(error, 'partners.errors.update')
          )
        )
      );
  }

  public deletePartner(partnerId: string): Observable<void> {
    return this.api.deletePartner(partnerId).pipe(
      catchError((error: HttpErrorResponse) => {
        const hasCustomers = problemDetailCode(error) === PARTNER_ERROR_CODE.HAS_ORGANIZATIONS;

        return throwError(() =>
          AppError.from(error, hasCustomers ? 'partners.errors.delete-has-customers' : 'partners.errors.delete')
        );
      })
    );
  }
}

function toUserError(error: HttpErrorResponse, translationKey: string): AppError {
  return AppError.from(
    error,
    error.status === HttpStatusCode.NotFound ? 'partners.errors.user-not-found' : translationKey
  );
}

function toPartnerUser({
  id,
  first_name,
  last_name,
  email,
  phone_number,
  status,
  roles,
  created_at,
}: PartnerUserResponse): PartnerUser {
  const admin = roles.some(({ name }) => name === PARTNER_USER_ROLE.ADMIN);

  return {
    id,
    full_name: [first_name, last_name].filter(Boolean).join(' '),
    first_name,
    last_name,
    email,
    phone_number,
    role: admin ? PARTNER_USER_ROLE.ADMIN : PARTNER_USER_ROLE.USER,
    status,
    created_at,
  };
}
